import { ArrowLeft, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";
import secureflexLogo from "@/assets/secureflex-logo-new.png";

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen app-bg font-outfit">
      {/* Header */}
      <div className="sticky top-0 z-50 border-b border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-bg))/0.95] backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-primary))] transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <img src={secureflexLogo} alt="SecureFlex" className="h-8" />
          <h1 className="font-bebas text-xl text-[hsl(var(--app-text-primary))] tracking-wider">PRIVACY POLICY</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="app-card rounded-2xl p-8 md:p-12 space-y-8">
          <div className="h-px -mx-8 md:-mx-12 -mt-8 md:-mt-12 mb-8" style={{ background: "linear-gradient(90deg, transparent, hsl(var(--app-gold)), transparent)" }} />

          <div className="text-center mb-10">
            <Shield className="w-10 h-10 mx-auto mb-3 text-[hsl(var(--app-gold))]" />
            <h2 className="font-bebas text-3xl text-[hsl(var(--app-text-primary))]">SECUREFLEX PRIVACY POLICY</h2>
            <p className="text-sm text-[hsl(var(--app-text-muted))] mt-2">Last Updated: 16 March 2026</p>
            <p className="text-sm text-[hsl(var(--app-text-muted))]">Effective Date: 16 March 2026</p>
          </div>

          {sections.map((section, i) => (
            <div key={i} className="space-y-3">
              <h3 className="font-bebas text-lg text-[hsl(var(--app-gold))] tracking-wider">{`${i + 1}. ${section.title}`}</h3>
              {section.content.map((item, j) => (
                typeof item === "string" ? (
                  <p key={j} className="text-sm leading-relaxed text-[hsl(var(--app-text-secondary))]">{item}</p>
                ) : (
                  <div key={j} className="ml-4 space-y-1.5">
                    {item.map((bullet, k) => (
                      <p key={k} className="text-sm leading-relaxed text-[hsl(var(--app-text-secondary))] flex gap-2">
                        <span className="text-[hsl(var(--app-gold))] flex-shrink-0">•</span>
                        <span>{bullet}</span>
                      </p>
                    ))}
                  </div>
                )
              ))}
            </div>
          ))}

          <div className="pt-8 border-t border-[hsl(var(--app-border-subtle))]">
            <p className="text-xs text-[hsl(var(--app-text-muted))] text-center">
              By using SecureFlex, you acknowledge that you have read and understood this Privacy Policy and consent to the processing of your personal information as described herein.
            </p>
            <p className="text-xs text-[hsl(var(--app-text-muted))] text-center mt-2">
              For privacy enquiries, contact us at <span className="text-[hsl(var(--app-gold))]">secureflexgroup@outlook.com</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

type ContentItem = string | string[];

const sections: { title: string; content: ContentItem[] }[] = [
  {
    title: "INTRODUCTION",
    content: [
      "SecureFlex (Pty) Ltd (\"SecureFlex\", \"we\", \"us\", or \"our\") is committed to protecting the privacy and personal information of our users, clients, and their employees. This Privacy Policy explains how we collect, use, process, store, and protect personal information when you use the SecureFlex platform (the \"Platform\").",
      "This policy is drafted in compliance with the Protection of Personal Information Act, 2013 (POPIA) of South Africa and, where applicable, the General Data Protection Regulation (GDPR) of the European Union.",
    ],
  },
  {
    title: "INFORMATION WE COLLECT",
    content: [
      "We collect the following categories of personal information:",
      [
        "Account Information: Full name, email address, phone number, company name, country, and login credentials.",
        "Biometric Data: Facial photographs and biometric face verification data used for officer identity confirmation during check-ins and shift clocking.",
        "Location Data: Real-time GPS coordinates of officers during active duty, including latitude, longitude, and geofence proximity data.",
        "Operational Data: Check-in records, patrol schedules, incident reports (including photographs, audio recordings, and descriptions), checkpoint scan data, and shift clock records.",
        "Device Information: Browser type, operating system, device identifiers, and IP addresses collected automatically during Platform use.",
        "Communication Data: Messages submitted through contact forms, support tickets, and any correspondence with SecureFlex.",
        "Payment Information: Subscription plan selection and billing history. Payment card details are processed by our third-party payment processor (Stripe) and are never stored on SecureFlex servers.",
      ],
    ],
  },
  {
    title: "HOW WE USE YOUR INFORMATION",
    content: [
      "We process your personal information for the following purposes:",
      [
        "To provide and operate the Platform, including real-time tracking, biometric verification, scheduling, and incident management.",
        "To create, maintain, and secure your user account.",
        "To process subscription payments and manage billing.",
        "To communicate with you regarding your account, service updates, security alerts, and customer support.",
        "To improve Platform performance, features, and user experience through aggregated analytics.",
        "To detect, prevent, and respond to security threats, fraud, and abuse.",
        "To comply with legal obligations and regulatory requirements.",
        "To enforce our Terms of Service and protect the rights, property, and safety of SecureFlex and its users.",
      ],
    ],
  },
  {
    title: "LEGAL BASIS FOR PROCESSING",
    content: [
      "We process personal information on the following legal grounds:",
      [
        "Consent: Where you have provided explicit consent for the processing of your personal information, particularly for biometric data collection.",
        "Contractual Necessity: Processing necessary for the performance of the service agreement between you and SecureFlex.",
        "Legitimate Interest: Processing necessary for our legitimate business interests, such as improving Platform security and functionality, provided these interests do not override your fundamental rights.",
        "Legal Obligation: Processing necessary to comply with applicable laws and regulations.",
      ],
    ],
  },
  {
    title: "BIOMETRIC DATA",
    content: [
      "SecureFlex processes biometric facial data as a core feature of the Platform for officer identity verification. We recognise the sensitive nature of this data and apply the following safeguards:",
      [
        "Biometric data is collected only with the informed consent of the individual.",
        "Facial photographs are stored securely in encrypted cloud storage with strict access controls.",
        "Biometric comparison is performed using secure, industry-standard algorithms.",
        "Biometric data is retained only for the duration of the officer's active employment and Platform use, and is deleted upon account termination or upon request.",
        "We do not sell, trade, or share biometric data with third parties for commercial purposes.",
      ],
    ],
  },
  {
    title: "DATA SHARING & THIRD PARTIES",
    content: [
      "We may share your personal information with the following categories of recipients:",
      [
        "Service Providers: Cloud hosting providers (for Platform infrastructure), payment processors (Stripe), mapping services, and email communication services, all bound by data processing agreements.",
        "Your Employer/Client: If you are an officer, your operational data (location, check-ins, incidents) is shared with your assigned security operator and site owner as necessary for Platform functionality.",
        "Legal Authorities: Where required by law, regulation, court order, or governmental request.",
        "Business Transfers: In connection with any merger, acquisition, or sale of SecureFlex assets, your personal information may be transferred, subject to this Privacy Policy.",
      ],
      "We do not sell your personal information to third parties for marketing or advertising purposes.",
    ],
  },
  {
    title: "DATA RETENTION",
    content: [
      "We retain personal information for as long as necessary to fulfil the purposes outlined in this policy, unless a longer retention period is required by law. Specifically:",
      [
        "Account data is retained for the duration of your active subscription and for 30 days following account termination.",
        "Operational data (check-ins, incidents, schedules) is retained for 24 months for audit and compliance purposes.",
        "Biometric data is deleted upon officer account termination or upon written request.",
        "Payment records are retained for 7 years as required by financial regulations.",
        "Support tickets and communications are retained for 12 months after resolution.",
      ],
    ],
  },
  {
    title: "DATA SECURITY",
    content: [
      "SecureFlex implements appropriate technical and organisational measures to protect your personal information against unauthorised access, alteration, disclosure, or destruction, including:",
      [
        "Encryption of data in transit (TLS/SSL) and at rest.",
        "Row-level security and access controls on all database tables.",
        "Secure authentication mechanisms including password hashing and session management.",
        "Regular security assessments and monitoring.",
        "Access to personal data is limited to authorised personnel on a need-to-know basis.",
      ],
      "While we implement robust security measures, no method of transmission or storage is 100% secure. We cannot guarantee absolute security of your personal information.",
    ],
  },
  {
    title: "YOUR RIGHTS",
    content: [
      "Subject to applicable law, you have the following rights regarding your personal information:",
      [
        "Right of Access: Request a copy of the personal information we hold about you.",
        "Right to Rectification: Request correction of inaccurate or incomplete personal information.",
        "Right to Deletion: Request deletion of your personal information, subject to legal retention requirements.",
        "Right to Restrict Processing: Request limitation of processing in certain circumstances.",
        "Right to Data Portability: Receive your personal information in a structured, machine-readable format.",
        "Right to Object: Object to processing based on legitimate interests.",
        "Right to Withdraw Consent: Withdraw previously given consent at any time, without affecting the lawfulness of processing based on consent before its withdrawal.",
      ],
      "To exercise any of these rights, please contact us at secureflexgroup@outlook.com. We will respond to your request within 30 days.",
    ],
  },
  {
    title: "COOKIES & TRACKING",
    content: [
      "The Platform uses essential cookies and local storage to maintain your session, preferences, and authentication state. These are strictly necessary for Platform functionality.",
      "We do not use third-party advertising cookies or tracking pixels. Analytics data is collected in aggregated, anonymised form and cannot be used to identify individual users.",
    ],
  },
  {
    title: "CHILDREN'S PRIVACY",
    content: [
      "The Platform is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from minors. If we become aware that we have inadvertently collected data from a minor, we will take immediate steps to delete such information.",
    ],
  },
  {
    title: "INTERNATIONAL DATA TRANSFERS",
    content: [
      "Your personal information may be processed and stored on servers located outside of South Africa, including in jurisdictions that may have different data protection standards. Where such transfers occur, we ensure appropriate safeguards are in place, including data processing agreements with standard contractual clauses, to protect your personal information in accordance with applicable law.",
    ],
  },
  {
    title: "CHANGES TO THIS POLICY",
    content: [
      "SecureFlex reserves the right to update this Privacy Policy at any time. Material changes will be communicated via email or through the Platform with at least 30 days' notice. The \"Last Updated\" date at the top of this policy indicates when it was most recently revised.",
      "Your continued use of the Platform following the effective date of any changes constitutes your acceptance of the revised policy.",
    ],
  },
  {
    title: "INFORMATION OFFICER",
    content: [
      "In accordance with POPIA, SecureFlex has appointed an Information Officer responsible for ensuring compliance with data protection legislation. For any privacy-related enquiries, complaints, or requests, please contact:",
      "SecureFlex (Pty) Ltd — Information Officer\nEmail: secureflexgroup@outlook.com\nAddress: Durban, KwaZulu-Natal, South Africa",
    ],
  },
];

export default PrivacyPolicy;
